import React from "react"
import { useEffect } from "react"
import { Box, Grid, Stack, Typography, FormControl, TextField, InputLabel, Divider } from "@mui/material"
import FileUpload from "./FileUploadBox.tsx"
import { FieldError } from "../ScoreCardValidation/ValidationErrors.tsx"
import type { ValidationError } from "../ScoreCardValidation/ValidationSchema.tsx"

interface OtherDetailsScoreCardProps {
  invoice: any
  setInvoice: any
  reportDetails: any
  uploadedFiles: Record<string, File[]>
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>, section: string) => void
  onRemoveFile: (section: string, index: number) => void
  validationErrors?: ValidationError[]
}

const OtherDetailsScoreCard: React.FC<OtherDetailsScoreCardProps> = ({
  invoice,
  setInvoice,
  reportDetails,
  uploadedFiles,
  onFileUpload,
  onRemoveFile,
  validationErrors = [],
}) => {
  console.log(uploadedFiles)
  // State for invoice fields
  // const [invoice, setInvoice] = useState({
  //   food: 0,
  //   bar: 0,
  //   gaming: 0,
  //   total: 0,
  // });

  // Populate invoice state from reportDetails on mount
  useEffect(() => {
    if (reportDetails?.reportHeader) {
      setInvoice({
        food: reportDetails.reportHeader?.spentFoodAmt || 0,
        bar: reportDetails.reportHeader?.spentBarAmt || 0,
        gaming: reportDetails.reportHeader?.spentGamingAmt || 0,
        total: reportDetails.reportHeader?.spentAmt || 0,
      })
    }
  }, [reportDetails])

  // Handle input changes & recalculate total
  const handleInputChange = (field: "food" | "bar" | "gaming", value: any) => {
    setInvoice((prevInvoice: any) => {
      const updatedValue = Number.parseFloat(value) || 0 // Convert to number, default to 0 if empty or invalid
      const updatedInvoice = {
        ...prevInvoice,
        [field]: updatedValue,
      }

      // Calculate total using the latest numeric values
      const newTotal =
        (field === "food" ? updatedValue : Number.parseFloat(updatedInvoice.food)) +
        (field === "bar" ? updatedValue : Number.parseFloat(updatedInvoice.bar)) +
        (field === "gaming" ? updatedValue : Number.parseFloat(updatedInvoice.gaming))

      return { ...updatedInvoice, total: newTotal.toFixed(2) } // Ensure 2 decimal places
    })
  }

  return (
    <Stack spacing={4}>
      <Box>
        <Typography variant="h5" fontWeight={600}>
          Task Photos
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your task photos here to better review.
        </Typography>
      </Box>

      <FileUpload
        section="taskPhotos"
        title="Task Photos"
        files={uploadedFiles.taskPhotos}
        onUpload={onFileUpload}
        onRemove={onRemoveFile}
        error={validationErrors.some((e) => e.field === "taskPhotos")}
        errorMessage={validationErrors.find((e) => e.field === "taskPhotos")?.message}
      />

      {reportDetails?.reportOtherDetails?.isNeedToHideInvoicePanel === 0 && (
        <Divider sx={{ mt: 2, borderBottom: '2px solid #ADB792' }} />
      )}

      {reportDetails?.reportOtherDetails?.isNeedToHideInvoicePanel === 0 && (
        <Box sx={{ mt: 2 }} className="invoiceDetails">
          <Typography variant="h5" fontWeight={600}>
            Invoice Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kindly provide your all Invoice Amount here. Invoice photos will be great option for faster approval.
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Food Amount */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth error={validationErrors.some((e) => e.field === "invoice_food")}>
                <InputLabel>Food</InputLabel>
                <TextField
                  // label="Food"
                  type="text"
                  value={invoice.food}
                  onChange={(e) => handleInputChange("food", Number(e.target.value) || 0)}
                  sx={{ "& .MuiInputBase-root": { backgroundColor: "#ffffff" } }}
                  error={validationErrors.some((e) => e.field === "invoice_food")}
                />
                <FieldError errors={validationErrors} field="invoice_food" />
              </FormControl>
            </Grid>

            {/* Bar Amount */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth error={validationErrors.some((e) => e.field === "invoice_bar")}>
                <InputLabel>Bar</InputLabel>
                <TextField
                  // label="Bar"
                  type="text"
                  value={invoice.bar}
                  onChange={(e) => handleInputChange("bar", Number(e.target.value) || 0)}
                  sx={{ "& .MuiInputBase-root": { backgroundColor: "#ffffff" } }}
                  error={validationErrors.some((e) => e.field === "invoice_bar")}
                />
                <FieldError errors={validationErrors} field="invoice_bar" />
              </FormControl>
            </Grid>

            {/* Gaming Amount */}
            <Grid item xs={12} md={3}>
              <FormControl fullWidth error={validationErrors.some((e) => e.field === "invoice_gaming")}>
                <InputLabel className="label">Gaming</InputLabel>
                <TextField
                  // label="Gaming"
                  type="text"
                  value={invoice.gaming}
                  onChange={(e) => handleInputChange("gaming", Number(e.target.value) || 0)}
                  sx={{ "& .MuiInputBase-root": { backgroundColor: "#ffffff" } }}
                  error={validationErrors.some((e) => e.field === "invoice_gaming")}
                />
                <FieldError errors={validationErrors} field="invoice_gaming" />
              </FormControl>
            </Grid>

            {/* Total Amount (Read-Only) */}
            <Grid item xs={12} md={3}>
              <FormControl
                fullWidth
                error={validationErrors.some((e) => e.field === "invoice_total")}
                data-field="invoice_total"
              >
                <InputLabel>Total</InputLabel>
                <TextField
                  // label="Total"
                  type="text"
                  value={invoice.total}
                  disabled
                  sx={{ "& .MuiInputBase-root": { backgroundColor: "#f0f0f0" } }}
                  error={validationErrors.some((e) => e.field === "invoice_total")}
                />
                <FieldError errors={validationErrors} field="invoice_total" />
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }} data-field="invoiceReceipt">
            <FileUpload
              section="invoiceReceipt"
              title="Invoice/Receipt Upload"
              files={uploadedFiles.invoiceReceipt}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
              error={validationErrors.some((e) => e.field === "invoiceReceipt")}
              errorMessage={validationErrors.find((e) => e.field === "invoiceReceipt")?.message}
            />
          </Box>
        </Box>
      )}

      <Divider sx={{ mt: 2, borderBottom: '2px solid #ADB792' }} />

      <Box sx={{ mt: 2 }} className="locationDetails">
        <Typography variant="h5" fontWeight={600}>
          Location Details
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload your Check-In & Check-Out details so we can pinpoint the details exactly.
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1, width: "100%" }}>
          <Grid item xs={12} md={6} sx={{minWidth: "48%"}}>
            <FileUpload
              section="checkInFile"
              title="Check-In File"
              files={uploadedFiles.checkInFile}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
              error={validationErrors.some((e) => e.field === "checkInFile")}
              errorMessage={validationErrors.find((e) => e.field === "checkInFile")?.message}
            />
          </Grid>
          <Grid item xs={12} md={6} sx={{minWidth: "49%"}}>
            <FileUpload
              section="checkOutFile"
              title="Check-Out File"
              files={uploadedFiles.checkOutFile}
              onUpload={onFileUpload}
              onRemove={onRemoveFile}
              error={validationErrors.some((e) => e.field === "checkOutFile")}
              errorMessage={validationErrors.find((e) => e.field === "checkOutFile")?.message}
            />
          </Grid>
        </Grid>
      </Box>
      <Divider sx={{ mt: 2, borderBottom: '2px solid #ADB792' }} />

      {reportDetails?.reportHeader?.allocationClientGroup === "ALDI" && 
        <Box sx={{ mt: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Bag Images
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FileUpload
                section="shopperBagImages"
                title="Bag Images"
                files={uploadedFiles.shopperBagImages}
                onUpload={onFileUpload}
                onRemove={onRemoveFile}
                error={validationErrors.some((e) => e.field === "shopperBagImages")}
                errorMessage={
                  validationErrors.find((e) => e.field === "shopperBagImages")
                    ?.message
                }
              />
              <FieldError errors={validationErrors} field="shopperBagImages" />
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2, borderBottom: "2px solid #ADB792" }} />
        </Box>
      }
    </Stack>
  )
}

export default OtherDetailsScoreCard
